import {
  ArrowLeftOutlined,
  AudioOutlined,
  CheckCircleFilled,
  DownloadOutlined,
  FolderOpenOutlined,
  HeartOutlined,
  InfoCircleOutlined,
  SoundOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Alert, Button, Checkbox, Image, Input, Skeleton, Tag, Upload, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import {
  confirmVirtualIpVoice,
  confirmVirtualIpVoiceNone,
  generateVirtualIpVoiceCandidates,
  getVirtualIpCharacterSelectionWorkspace,
  getVirtualIpDraft,
  getVirtualIpVoiceWorkspace,
  saveVirtualIpVoiceIntake,
  selectVirtualIpVoiceCandidate,
  uploadVirtualIpVoiceReferenceAudio,
  type SaasApiError,
  type VirtualIpVoiceCreationMode,
  type VirtualIpVoiceMode,
  type VirtualIpVoiceWorkspace,
} from '../../api'
import { VIRTUAL_IP_FLOW_TOTAL_STEPS } from './virtualIpFlow'
import VirtualIpStepper from './VirtualIpStepper'
import { useResolvedVirtualIpProgress } from './useResolvedVirtualIpProgress'
import { useVirtualIpWorkflowContext } from './VirtualIpWorkflowContext'
import { virtualIpFirstRegenerationNotice, virtualIpResumePath } from './virtualIpProgress'
import VoiceAudioPlayer from './VoiceAudioPlayer'
import {
  formatVoiceGenerateError,
  isVoiceCandidateSelectable,
  downloadVoiceCandidateAudio,
  resolveCandidatePlaybackUiState,
} from './virtualIpVoiceCandidateUi'
import {
  canConfirmVoiceSelection,
  isVoiceStep6EditingLocked,
  isVoiceStep6GeneratingLocked,
  resolveSelectedCandidateId,
  resolveVoiceStep6Phase,
  voiceStep6SidebarHintKey,
} from './virtualIpVoiceStep6Interaction'
import './virtualIp.css'

type ModeMetaItem = {
  value: VirtualIpVoiceCreationMode
  icon: ReactNode
  title: string
  description: string
  benefits: string[]
}

function buildModeMeta(t: (key: string, options?: { returnObjects?: boolean }) => string | string[]): ModeMetaItem[] {
  return [
    {
      value: 'DESIGN_FROM_TEXT',
      icon: <ThunderboltOutlined />,
      title: t('step6.modes.DESIGN_FROM_TEXT.title') as string,
      description: t('step6.modes.DESIGN_FROM_TEXT.description') as string,
      benefits: t('step6.modes.DESIGN_FROM_TEXT.benefits', { returnObjects: true }) as string[],
    },
    {
      value: 'CLONE_FROM_REFERENCE',
      icon: <FolderOpenOutlined />,
      title: t('step6.modes.CLONE_FROM_REFERENCE.title') as string,
      description: t('step6.modes.CLONE_FROM_REFERENCE.description') as string,
      benefits: t('step6.modes.CLONE_FROM_REFERENCE.benefits', { returnObjects: true }) as string[],
    },
    {
      value: 'CLONE_SELF',
      icon: <UserOutlined />,
      title: t('step6.modes.CLONE_SELF.title') as string,
      description: t('step6.modes.CLONE_SELF.description') as string,
      benefits: t('step6.modes.CLONE_SELF.benefits', { returnObjects: true }) as string[],
    },
  ]
}

function buildNoneModeMeta(t: (key: string, options?: { returnObjects?: boolean }) => string | string[]) {
  return {
    value: 'NONE' as const,
    icon: <SoundOutlined />,
    title: t('step6.noneModeTitle') as string,
    description: t('step6.modes.NONE.description') as string,
    benefits: t('step6.modes.NONE.benefits', { returnObjects: true }) as string[],
  }
}

type Step7ModeChoice = VirtualIpVoiceCreationMode | 'NONE'

function resolveStep7Mode(voice: VirtualIpVoiceWorkspace): Step7ModeChoice {
  const mode = voice.voice_mode || voice.profile?.voice_mode
  if (mode === 'NONE') return 'NONE'
  return (voice.profile?.creation_mode || voice.recommended_creation_mode || 'DESIGN_FROM_TEXT') as VirtualIpVoiceCreationMode
}

function slotLetter(index: number): string {
  return String.fromCharCode(65 + index)
}

function creationModeToVoiceMode(mode: VirtualIpVoiceCreationMode): VirtualIpVoiceMode {
  if (mode === 'CLONE_FROM_REFERENCE') return 'REFERENCE'
  if (mode === 'CLONE_SELF') return 'CLONED'
  return 'AI_GENERATED'
}

function applyVoiceState(
  voice: VirtualIpVoiceWorkspace,
  setters: {
    setVoice: (v: VirtualIpVoiceWorkspace) => void
    setCreationMode: (m: Step7ModeChoice) => void
    setVoiceBrief: (s: string) => void
    setPreviewText: (s: string) => void
    setRightsConfirmed: (b: boolean) => void
  },
) {
  setters.setVoice(voice)
  setters.setCreationMode(resolveStep7Mode(voice))
  setters.setVoiceBrief(voice.profile?.voice_brief || '')
  setters.setPreviewText(voice.profile?.preview_text || '')
  setters.setRightsConfirmed(Boolean(voice.profile?.voice_rights_confirmed))
}

export default function VirtualIpStep6VoicePage() {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])

  const { workspaceId = '', projectId = '' } = useParams()
  const navigate = useNavigate()
  const [draftId, setDraftId] = useState('')
  const [voice, setVoice] = useState<VirtualIpVoiceWorkspace | null>(null)
  const [creationMode, setCreationMode] = useState<Step7ModeChoice>('DESIGN_FROM_TEXT')
  const [voiceBrief, setVoiceBrief] = useState('')
  const [previewText, setPreviewText] = useState('')
  const [rightsConfirmed, setRightsConfirmed] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)
  const activeAudioRef = useRef<HTMLAudioElement | null>(null)
  const generatingRef = useRef(false)

  const modeMeta = useMemo(() => buildModeMeta(t), [t])
  const noneModeMeta = useMemo(() => buildNoneModeMeta(t), [t])

  const setters = useMemo(
    () => ({ setVoice, setCreationMode, setVoiceBrief, setPreviewText, setRightsConfirmed }),
    [],
  )

  const reload = useCallback(async () => {
    const dRes = await getVirtualIpDraft(workspaceId, projectId)
    setDraftId(dRes.data.id)
    const sel = await getVirtualIpCharacterSelectionWorkspace(workspaceId, dRes.data.id)
    if (sel.data.selection?.selected_id) {
      const vRes = await getVirtualIpVoiceWorkspace(workspaceId, dRes.data.id)
      applyVoiceState(vRes.data, setters)
    } else {
      setVoice(null)
    }
  }, [workspaceId, setters])

  useEffect(() => {
    if (!workspaceId) return
    let cancelled = false
    setLoading(true)
    reload()
      .catch((e) => !cancelled && setLoadError((e as SaasApiError).message || t('common.loadFailed')))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [workspaceId, reload])

  const onCandidatePlay = (el: HTMLAudioElement | null) => {
    if (!el) return
    if (activeAudioRef.current && activeAudioRef.current !== el) {
      activeAudioRef.current.pause()
    }
    activeAudioRef.current = el
  }

  useEffect(() => () => activeAudioRef.current?.pause(), [])
  const { refreshProgress, draft: workflowDraft } = useVirtualIpWorkflowContext()

  const progress = useResolvedVirtualIpProgress()
  const regenNotice = virtualIpFirstRegenerationNotice(progress)

  const isNoneMode = creationMode === 'NONE'
  const needsRights = !isNoneMode && creationMode === 'CLONE_SELF'
  const workflowComplete = Boolean(workflowDraft?.workflow_confirmed_at)
  const voiceEditingLocked = isVoiceStep6EditingLocked({ workflowComplete })
  const generatingLocked = isVoiceStep6GeneratingLocked({ generating })
  const formLocked = voiceEditingLocked || generatingLocked
  const voiceConfirmed = Boolean(voice?.voice_confirmed)
  const selectedCandidateId = resolveSelectedCandidateId(voice)
  const candidates = voice?.candidates ?? voice?.latest_batch?.candidates ?? []
  const interactionPhase = resolveVoiceStep6Phase({
    generating,
    candidatesCount: candidates.length,
    selectedCandidateId,
    voiceConfirmed,
  })
  const canConfirmVoice = canConfirmVoiceSelection({
    selectedCandidateId,
    voiceConfirmed,
    isNoneMode,
  })
  const canConfirmNone = isNoneMode && !voiceConfirmed
  const canConfirm = canConfirmVoice || canConfirmNone
  const sidebarHintKey = voiceStep6SidebarHintKey({
    voiceConfirmed,
    isNoneMode,
    selectedCandidateId,
    canConfirm,
  })
  const sidebarHint = sidebarHintKey ? t(sidebarHintKey) : null

  if (!loading && !loadError && !progress.unlocked_steps.includes(7) && !progress.completed_steps.includes(7)) {
    return <Navigate to={virtualIpResumePath(workspaceId, progress, projectId)} replace />
  }

  const persistIntake = async (overrides?: {
    creation_mode?: VirtualIpVoiceCreationMode
    voice_mode?: VirtualIpVoiceMode
    voice_brief?: string
    preview_text?: string
    voice_rights_confirmed?: boolean
    reference_audio_asset_ids?: string[]
  }) => {
    if (!draftId) return null
    setSaving(true)
    try {
      const res = await saveVirtualIpVoiceIntake(workspaceId, draftId, {
        creation_mode: overrides?.creation_mode ?? (isNoneMode ? undefined : creationMode),
        voice_mode: overrides?.voice_mode ?? (isNoneMode ? 'NONE' : undefined),
        voice_brief: overrides?.voice_brief ?? voiceBrief,
        preview_text: overrides?.preview_text ?? previewText,
        voice_rights_confirmed: overrides?.voice_rights_confirmed ?? (needsRights ? rightsConfirmed : undefined),
        reference_audio_asset_ids: overrides?.reference_audio_asset_ids,
      })
      applyVoiceState(res.data, setters)
      setGenerateError(null)
      return res.data
    } catch (e) {
      message.error((e as SaasApiError).message || t('common.saveFailed'))
      return null
    } finally {
      setSaving(false)
    }
  }

  const onSelectMode = async (mode: Step7ModeChoice) => {
    setCreationMode(mode)
    if (mode === 'NONE') {
      await persistIntake({ voice_mode: 'NONE' })
    } else {
      await persistIntake({
        creation_mode: mode,
        voice_mode: creationModeToVoiceMode(mode),
      })
    }
  }

  const onGenerate = async () => {
    if (!draftId || generatingRef.current) return
    generatingRef.current = true
    setGenerating(true)
    setGenerateError(null)
    try {
      const saved = await persistIntake()
      if (!saved) return
      const genRes = await generateVirtualIpVoiceCandidates(workspaceId, draftId, 3)
      applyVoiceState(genRes.data, setters)
    } catch (e) {
      const err = e as SaasApiError
      setGenerateError(formatVoiceGenerateError(err, t))
      try {
        await reload()
      } catch {
        /* ignore */
      }
    } finally {
      generatingRef.current = false
      setGenerating(false)
    }
  }

  const onSelectCandidate = async (candidateId: string) => {
    if (!draftId) return
    const candidate = candidates.find((c) => c.candidate_id === candidateId)
    if (!candidate || !isVoiceCandidateSelectable(candidate)) {
      message.warning(t('step6.waitPreviewReady'))
      return
    }
    setSaving(true)
    try {
      const res = await selectVirtualIpVoiceCandidate(workspaceId, draftId, candidateId)
      applyVoiceState(res.data, setters)
      message.success(t('step6.voiceSelected'))
    } catch (e) {
      message.error((e as SaasApiError).message || t('common.selectFailed'))
    } finally {
      setSaving(false)
    }
  }

  const onConfirm = async () => {
    if (!draftId) return
    setConfirming(true)
    try {
      const res = isNoneMode
        ? await confirmVirtualIpVoiceNone(workspaceId, draftId)
        : await confirmVirtualIpVoice(workspaceId, draftId)
      applyVoiceState(res.data, setters)
      await refreshProgress()
      message.success(isNoneMode ? t('step6.noneConfirmed') : t('step6.voiceConfirmed'))
      navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-7`)
    } catch (e) {
      message.error((e as SaasApiError).message || t('common.confirmFailed'))
    } finally {
      setConfirming(false)
    }
  }

  const onUploadAudio = async (file: File) => {
    if (!draftId) return false
    try {
      const uploaded = await uploadVirtualIpVoiceReferenceAudio(workspaceId, draftId, file)
      const existing = voice?.profile?.reference_audio_asset_ids ?? []
      await persistIntake({
        reference_audio_asset_ids: [...existing, uploaded.data.asset_id],
        voice_rights_confirmed: rightsConfirmed,
      })
      message.success(t('step6.referenceUploaded'))
    } catch (e) {
      message.error((e as SaasApiError).message || t('step6.uploadFailed'))
    }
    return false
  }

  const character = voice?.selected_character
  const notReady = voice != null && !voice.voice_execution_ready

  return (
    <div
      className="tc-vip-page tc-vip-step6-voice-page"
      data-testid="virtual-ip-step6-voice-page"
      data-voice-interaction-phase={interactionPhase}
    >
      <div className="tc-page-header">
        <div>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-5`)}
            style={{ padding: 0, height: 'auto', marginBottom: 8 }}
          >
            {t('common.backToPrevious')}
          </Button>
          <h1 className="tc-page-title">{t('common.pageTitle')}</h1>
          <div className="tc-vip-step-label">
            <span>STEP 6 / {VIRTUAL_IP_FLOW_TOTAL_STEPS}</span>
            <span className="tc-vip-step-title">{t('step6.pageTitleVoice')}</span>
          </div>
          <p className="tc-page-desc">{t('step6.pageDescVoice')}</p>
        </div>
      </div>

      <VirtualIpStepper currentStep={6} progress={progress} />

      {regenNotice && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('common.stepsNeedRegeneration')} description={t(regenNotice.reasonKey)}
          data-testid="virtual-ip-regeneration-notice"
        />
      )}

      {loading && (
        <div className="tc-data-card tc-vip-card">
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      )}

      {!loading && loadError && <Alert type="error" showIcon message={t('common.loadFailed')} description={loadError} />}

      {!loading && !loadError && !character && (
        <Alert
          type="warning"
          showIcon
          message={t('step6.noCharacterTitle')} description={t('step6.noCharacterDesc')} action={
            <Button size="small" onClick={() => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-5`)}>
              {t('common.backToStep', { step: 5 })}
            </Button>
          }
        />
      )}

      {!loading && !loadError && character && (
        <div className="tc-vip-layout">
          <div className="tc-vip-main">
            <div className="tc-data-card tc-vip-card" data-testid="vip-step7-selected-character">
              <div className="tc-vip-card-title">
                {t('step6.selectedCharacterTitle')}
                {character.candidate_label && (
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {character.candidate_label}
                  </Tag>
                )}
              </div>
              <div className="tc-vip-step7-character-row">
                {character.thumbnail_url && (
                  <Image
                    src={character.thumbnail_url}
                    width={88}
                    height={88}
                    style={{ objectFit: 'cover', borderRadius: 12 }}
                    preview={false}
                  />
                )}
                <div>
                  <div className="tc-vip-step7-character-name">{character.ip_name || t('common.unnamedCharacter')}</div>
                  {character.core_positioning && (
                    <div className="tc-vip-card-desc" style={{ marginTop: 4 }}>
                      {character.core_positioning}
                    </div>
                  )}
                  {character.persona_traits?.length > 0 && (
                    <div className="tc-vip-step7-trait-row">
                      {character.persona_traits.map((t) => (
                        <Tag key={t}>{t}</Tag>
                      ))}
                    </div>
                  )}
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, marginTop: 6 }}
                    onClick={() => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-5`)}
                    data-testid="vip-step7-view-character-details"
                  >
                    {t('common.viewDetails')}
                  </Button>
                </div>
              </div>
            </div>

            <div className="tc-data-card tc-vip-card" data-testid="vip-step7-mode-card">
              <div className="tc-vip-card-title">{t('step6.modeSelectionTitle')}</div>
              <p className="tc-vip-card-desc">{t('step6.modeSelectionDesc')}</p>
              <div className="tc-vip-step5-mode-grid tc-vip-step7-mode-grid">
                {modeMeta.map((m) => {
                  const isSelected = creationMode === m.value
                  const isRecommended = !isNoneMode && (voice?.recommended_creation_mode || 'DESIGN_FROM_TEXT') === m.value
                  return (
                    <button
                      key={m.value}
                      type="button"
                      className={`tc-vip-step5-mode-card ${isSelected ? 'is-selected' : ''}`}
                      disabled={saving || formLocked}
                      onClick={() => onSelectMode(m.value)}
                      data-testid={`vip-step7-mode-${m.value}`}
                    >
                      {isSelected && (
                        <span className="tc-vip-proposal-card-check">
                          <CheckCircleFilled />
                        </span>
                      )}
                      <span className="tc-vip-step5-mode-icon">{m.icon}</span>
                      <span className="tc-vip-step5-mode-label">{m.title}</span>
                      {isRecommended && <span className="tc-vip-step5-mode-badge is-recommended">{t('common.recommended')}</span>}
                      <span className="tc-vip-step5-mode-desc">{m.description}</span>
                      <ul className="tc-vip-step7-mode-benefits">
                        {m.benefits.map((b) => (
                          <li key={b}>{b}</li>
                        ))}
                      </ul>
                    </button>
                  )
                })}
                {(() => {
                  const m = noneModeMeta
                  const isSelected = creationMode === m.value
                  return (
                    <button
                      key={m.value}
                      type="button"
                      className={`tc-vip-step5-mode-card ${isSelected ? 'is-selected' : ''}`}
                      disabled={saving || formLocked}
                      onClick={() => onSelectMode(m.value)}
                      data-testid={`vip-step7-mode-${m.value}`}
                    >
                      {isSelected && (
                        <span className="tc-vip-proposal-card-check">
                          <CheckCircleFilled />
                        </span>
                      )}
                      <span className="tc-vip-step5-mode-icon">{m.icon}</span>
                      <span className="tc-vip-step5-mode-label">{m.title}</span>
                      <span className="tc-vip-step5-mode-desc">{m.description}</span>
                      <ul className="tc-vip-step7-mode-benefits">
                        {m.benefits.map((b) => (
                          <li key={b}>{b}</li>
                        ))}
                      </ul>
                    </button>
                  )
                })()}
              </div>
            </div>

            {isNoneMode && (
              <div className="tc-data-card tc-vip-card" data-testid="vip-step7-none-panel">
                <Alert
                  type="info"
                  showIcon
                  message={t('step6.noneModeTitle')} description={t('step6.noneModeDesc')}
                />
              </div>
            )}

            {!isNoneMode && creationMode === 'DESIGN_FROM_TEXT' && (
              <div className="tc-data-card tc-vip-card" data-testid="vip-step7-voice-brief">
                <div className="tc-vip-card-title">{t('step6.voiceBriefTitle')}</div>
                <p className="tc-vip-card-desc">{t('step6.voiceBriefDesc')}</p>
                <Input.TextArea
                  rows={4}
                  value={voiceBrief}
                  disabled={formLocked}
                  onChange={(e) => setVoiceBrief(e.target.value)}
                  onBlur={() => persistIntake({ voice_brief: voiceBrief })}
                  data-testid="vip-step7-voice-brief-input"
                />
              </div>
            )}

            {!isNoneMode && (creationMode === 'CLONE_FROM_REFERENCE' || creationMode === 'CLONE_SELF') && (
              <div className="tc-data-card tc-vip-card" data-testid="vip-step7-reference-audio">
                <div className="tc-vip-card-title">
                  {creationMode === 'CLONE_SELF' ? t('step6.uploadMyVoice') : t('step6.uploadReferenceVoice')}
                </div>
                <p className="tc-vip-card-desc">
                  {t('step6.uploadAudioDesc')}
                  {creationMode === 'CLONE_FROM_REFERENCE' && (
                    <span style={{ display: 'block', marginTop: 8 }}>
                      {t('step6.uploadReferenceNote')}
                    </span>
                  )}
                </p>
                <Upload
                  accept=".wav,.mp3,.m4a,.aac,audio/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    void onUploadAudio(file)
                    return false
                  }}
                  disabled={formLocked}
                >
                  <Button icon={<UploadOutlined />} disabled={formLocked} data-testid="vip-step7-upload-audio">
                    {t('step6.selectAudioFile')}
                  </Button>
                </Upload>
                <div className="tc-vip-step7-audio-list">
                  {(voice?.profile?.reference_audio_assets ?? []).map((a) => (
                    <div key={a.asset_id} className="tc-vip-step7-audio-item" data-testid="vip-step7-audio-asset">
                      <AudioOutlined />
                      <div>
                        <div>{a.filename || a.asset_id}</div>
                        <div className="tc-vip-card-hint">
                          {(a.size_bytes / 1024).toFixed(1)} KB
                          {a.duration_ms != null ? ` · ${(a.duration_ms / 1000).toFixed(1)}s` : ''}
                        </div>
                      </div>
                      {a.playback_url && (
                        <VoiceAudioPlayer playbackUrl={a.playback_url} testId="vip-step7-reference-player" />
                      )}
                    </div>
                  ))}
                </div>
                <Checkbox
                  checked={rightsConfirmed}
                  disabled={formLocked || creationMode === 'CLONE_FROM_REFERENCE'}
                  onChange={(e) => {
                    const next = e.target.checked
                    setRightsConfirmed(next)
                    void persistIntake({ voice_rights_confirmed: next })
                  }}
                  data-testid="vip-step7-rights-checkbox"
                  style={{ marginTop: 12, display: creationMode === 'CLONE_SELF' ? undefined : 'none' }}
                >
                  {t('step6.selfVoiceDeclaration')}
                </Checkbox>
              </div>
            )}

            {!isNoneMode && (
            <div className="tc-data-card tc-vip-card" data-testid="vip-step7-generate-section">
              <div className="tc-vip-card-head">
                <div>
                  <div className="tc-vip-card-title">{t('step6.generateSectionTitle')}</div>
                  <p className="tc-vip-card-desc">
                    {t('step6.generateThreeNote')}
                  </p>
                </div>
                <Button
                  type="primary"
                  loading={generating}
                  disabled={formLocked || generating || (needsRights && !rightsConfirmed)}
                  onClick={() => void onGenerate()}
                  data-testid="vip-step7-generate-button"
                >
                  {generating ? t('step5.generatingEllipsis') : t('step6.generateSectionTitle')}
                </Button>
              </div>

              {notReady && (
                <Alert
                  type="warning"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  style={{ marginBottom: 12 }}
                  message={t('step6.capabilityNotReady')} description={voice?.voice_execution_message || t('step6.noExecutionNode')}
                  data-testid="vip-step7-not-ready-banner"
                />
              )}
              {generateError && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={generateError}
                  data-testid="vip-step7-generate-error"
                />
              )}

              <div className="tc-vip-step7-audition-text" data-testid="vip-step7-audition-text">
                <div className="tc-vip-card-title" style={{ fontSize: 14 }}>
                  {t('step6.previewCopy')}
                </div>
                <Input.TextArea
                  rows={2}
                  value={previewText}
                  disabled={formLocked}
                  onChange={(e) => setPreviewText(e.target.value)}
                  onBlur={() => persistIntake({ preview_text: previewText })}
                  data-testid="vip-step7-preview-text-input"
                />
              </div>

              {candidates.length > 0 ? (
                <>
                  <div className="tc-vip-card-title" style={{ marginTop: 16 }}>
                    {t('step6.aiVoicePlans', { count: candidates.length })}
                  </div>
                  <div className="tc-vip-step7-candidate-grid">
                    {candidates.map((c, idx) => {
                      const selected = selectedCandidateId === c.candidate_id
                      const playbackState = resolveCandidatePlaybackUiState(c)
                      const selectable = isVoiceCandidateSelectable(c)
                      return (
                        <div
                          key={c.candidate_id}
                          className={`tc-vip-step7-candidate-card ${selected ? 'is-selected' : ''}`}
                          data-testid={`vip-step7-candidate-${slotLetter(idx)}`}
                          data-candidate-status={c.status}
                        >
                          <div className="tc-vip-step7-candidate-head">
                            <strong>
                              {slotLetter(idx)}. {c.display_name}
                            </strong>
                            <div className="tc-vip-step7-candidate-head-right">
                              {selected && <Tag color="blue">{t('common.selected')}</Tag>}
                              {playbackState === 'pending' && <Tag>{t('jobStatus.generating')}</Tag>}
                              {playbackState === 'failed' && <Tag color="error">{t('common.failed')}</Tag>}
                              {playbackState === 'ready' && c.audio?.playback_url ? (
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<DownloadOutlined />}
                                  aria-label={t('step6.downloadPreview')} title={t('step6.downloadPreview')} disabled={generatingLocked || voiceEditingLocked}
                                  onClick={() => downloadVoiceCandidateAudio(c.audio!.playback_url)}
                                  data-testid={`vip-step7-candidate-download-${slotLetter(idx)}`}
                                  className="tc-vip-step7-candidate-download"
                                />
                              ) : null}
                            </div>
                          </div>
                          <div className="tc-vip-step7-trait-row">
                            {(c.trait_tags || []).map((t) => (
                              <Tag key={t}>{t}</Tag>
                            ))}
                          </div>
                          <p className="tc-vip-card-desc">{c.description}</p>
                          {playbackState === 'pending' ? (
                            <div className="tc-vip-card-hint" data-testid="vip-step7-candidate-pending">
                              {t('step6.generatingPreview')}
                            </div>
                          ) : playbackState === 'failed' ? (
                            <div className="tc-vip-card-hint" data-testid="vip-step7-candidate-failed">
                              {t('step6.previewFailed')}
                            </div>
                          ) : playbackState === 'ready' && c.audio?.playback_url ? (
                            <VoiceAudioPlayer
                              playbackUrl={c.audio.playback_url}
                              onPlay={onCandidatePlay}
                            />
                          ) : (
                            <div className="tc-vip-card-hint">{t('step6.noPreviewAudio')}</div>
                          )}
                          <div className="tc-vip-step7-candidate-actions">
                            <Button
                              type={selected ? 'default' : 'primary'}
                              disabled={generatingLocked || !selectable}
                              onClick={() => void onSelectCandidate(c.candidate_id)}
                              data-testid={`vip-step7-candidate-select-${slotLetter(idx)}`}
                            >
                              {selected ? t('common.selected') : t('step6.selectThisVoice')}
                            </Button>
                            <Button type="text" icon={<HeartOutlined />} disabled aria-label={t('common.favoriteComingSoon')} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              ) : (
                <div className="tc-vip-step7-empty-candidates" data-testid="vip-step7-empty-candidates">
                  <SoundOutlined style={{ fontSize: 28, opacity: 0.5 }} />
                  <div>{t('step6.noVoicePlansYet')}</div>
                </div>
              )}
            </div>
            )}
          </div>

          <aside className="tc-vip-side">
            <div className="tc-data-card tc-vip-card">
              <div className="tc-vip-card-title">{t('step6.tipsTitle')}</div>
              <ol className="tc-vip-step7-tips">
                <li>{t('step6.designTips.0')}</li>
                <li>{t('step6.designTips.1')}</li>
                <li>{t('step6.designTips.2')}</li>
              </ol>
            </div>
            <div className="tc-data-card tc-vip-card">
              <div className="tc-vip-card-title">{t('step6.cloneAboutTitle')}</div>
              <ul className="tc-vip-step7-tips">
                <li>{t('step6.cloneTipsList.0')}</li>
                <li>{t('step6.cloneTipsList.1')}</li>
                <li>{t('step6.cloneTipsList.2')}</li>
              </ul>
            </div>
            <div className="tc-data-card tc-vip-card">
              <div className="tc-vip-card-title">{t('step6.flowTitle')}</div>
              <ol className="tc-vip-step7-tips">
                <li>{t('step6.flowSteps.0')}</li>
                <li>{t('step6.generateSectionTitle')}</li>
                <li>{t('step6.flowSteps.2')}</li>
                <li>{t('step6.flowSteps.3')}</li>
              </ol>
            </div>
            <div className="tc-data-card tc-vip-card tc-vip-step7-cta-card">
              <Button
                type="primary"
                block
                size="large"
                disabled={!canConfirm}
                loading={confirming}
                onClick={() => void onConfirm()}
                data-testid="vip-step7-confirm-cta"
              >
                {isNoneMode ? t('step6.confirmNoneAndNext') : t('step6.confirmVoiceAndNext')}
              </Button>
              {sidebarHint ? (
                <div className="tc-vip-card-hint" style={{ marginTop: 8, textAlign: 'center' }} data-testid="vip-step6-sidebar-hint">
                  {sidebarHint}
                </div>
              ) : null}
              {voiceConfirmed ? (
                <Button
                  block
                  style={{ marginTop: 8 }}
                  type="default"
                  onClick={() => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-7`)}
                  data-testid="vip-step6-go-final-button"
                >
                  {t('common.nextToFinalize')}
                </Button>
              ) : null}
            </div>
          </aside>
        </div>
      )}
    </div>
  )
}
